import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { useAppDispatch } from '@/store/hooks';
import { showSnackbar } from '@/store/slices/snackbarSlice';
import { GgcButton } from '@/components/ui/ggc/button';
import { GgcEmptyState } from '@/components/ui/ggc/empty-state';
import { GgcActiveFilterChip } from '@/components/ui/ggc/active-filter-chip';
import { PageLoader } from '@/components/Spinner';
import { ChannelFilterSidebar } from './filters';
import { InfluencerTable } from './InfluencerTable';
import { getUsers, getUsersTotalPage } from '@/lib/api/dashboard.api';
import { addInfluencersToCampaign } from '@/lib/api/campaign.api';
import { buildUsersQueryParams } from '../utils';
import { INITIAL_FILTER } from '../constants';
import type { FilterState, Influencer, Campaign, CampaignPost, ColumnDef } from '../types';

/**
 * Columns surfaced in the search-and-add drawer: the all-influencers default set
 * minus campaign-only / editable columns (final offer, advertiser price, video type,
 * KPI, status, campaign stats) which need a campaign context that doesn't apply here.
 */
const DRAWER_COLUMNS: ColumnDef[] = [
  { id: 'rowNum',      label: '#',           visible: true, width: 40,  minWidth: 40  },
  { id: 'name',        label: 'Name',        visible: true, width: 160, minWidth: 100 },
  { id: 'socials',     label: 'Socials',     visible: true, width: 100, minWidth: 70  },
  { id: 'country',     label: 'Country',     visible: true, width: 70,  minWidth: 60  },
  { id: 'email',       label: 'Email',       visible: true, width: 160, minWidth: 100 },
  { id: 'estPrice',    label: 'Est. price',  visible: true, width: 90,  minWidth: 70  },
  { id: 'recentGames', label: 'Recent games',visible: true, width: 120, minWidth: 80  },
  { id: 'tags',        label: 'Tags',        visible: true, width: 100, minWidth: 70  },
  { id: 'subs',        label: 'Subscribers', visible: true, width: 90,  minWidth: 70  },
  { id: 'lva',         label: 'Last Activity', visible: true, width: 100, minWidth: 80 },
  { id: 'rav5',        label: 'Avg. Viewership (last 5)', visible: true, width: 100, minWidth: 80 },
];

interface AddInfluencersDrawerBodyProps {
  campaignId: string;
  isAdmin: boolean;
  campaigns: Campaign[];
  organizationId?: string;
  onClose: () => void;
  /** Called after a successful add so the parent refreshes its campaign list + status counts. */
  onAdded: (addedUserIds: string[]) => void;
}

type UsersResponse = { data?: Influencer[]; analysis?: Record<string, { posts?: CampaignPost[] }> } | Influencer[];

/** FilterState fields (string-valued) surfaced as removable condition chips. */
type StringFilterKey =
  | 'channelName' | 'email' | 'country' | 'language' | 'subscriberCount'
  | 'keywords' | 'tags' | 'categories' | 'inclusionComment' | 'recentGame'
  | 'recentGenre' | 'offer' | 'candidateGroupName'
  | 'isRegistered' | 'hasRates' | 'hasDemographic' | 'hasStreams';

/**
 * Right-drawer body: searches the system-wide influencer list (left sidebar filters +
 * shared InfluencerTable) and adds the checked influencers to the current campaign.
 * Holds its own local state, independent of the campaign dashboard's list.
 */
export function AddInfluencersDrawerBody({
  campaignId, isAdmin, campaigns, organizationId, onClose, onAdded,
}: AddInfluencersDrawerBodyProps) {
  const { t } = useI18nText();
  const dispatch = useAppDispatch();

  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER);
  const [appliedFilter, setAppliedFilter] = useState<FilterState>(INITIAL_FILTER);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [perUserAnalysis, setPerUserAnalysis] = useState<Record<string, { posts?: CampaignPost[] }>>({});
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  // The drawer is for search-and-add, so nothing is fetched until the user runs
  // a search. Until then we show a prompt instead of the (heavy) full list.
  const [hasSearched, setHasSearched] = useState(false);
  const [adding, setAdding] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expandedSiblings, setExpandedSiblings] = useState<Set<string>>(new Set());
  const pageSizeRef = useRef(50);
  const genRef = useRef(0);

  const campaignName = campaigns.find((c) => (c._id ?? c.id) === campaignId)?.name ?? '';

  const load = useCallback(async (f: FilterState) => {
    setHasSearched(true);
    const gen = ++genRef.current;
    setLoading(true);
    try {
      const apiParams = buildUsersQueryParams(f, 'all');
      const [usersResp, pagesResp] = await Promise.all([
        getUsers(apiParams) as Promise<UsersResponse>,
        getUsersTotalPage(apiParams) as Promise<{ totalPages?: number }>,
      ]);
      if (gen !== genRef.current) return; // stale response — a newer query started
      const users = Array.isArray(usersResp) ? usersResp : (usersResp?.data ?? []);
      const analysis = Array.isArray(usersResp) ? {} : (usersResp?.analysis ?? {});
      if (f.page === 1 && users.length > 0) pageSizeRef.current = users.length;
      setInfluencers(users);
      setPerUserAnalysis(analysis);
      setTotalPages(typeof pagesResp === 'object' ? (pagesResp?.totalPages ?? 1) : 1);
    } catch {
      if (gen !== genRef.current) return;
      setInfluencers([]);
      setPerUserAnalysis({});
      setTotalPages(1);
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, []);

  // No initial fetch — the drawer's purpose is search-and-add, so the full
  // influencer list is only loaded once the user applies search conditions
  // (handleApply / pagination / sort). Until then the body shows a prompt.

  // Close the confirm popover when clicking outside of the add button area.
  useEffect(() => {
    if (!confirmOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.add-influencers-drawer__add-wrap')) {
        setConfirmOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [confirmOpen]);

  const handleApply = useCallback(() => {
    const f = { ...filter, page: 1 };
    setAppliedFilter(f);
    void load(f);
  }, [filter, load]);

  const handleClear = useCallback(() => {
    setFilter(INITIAL_FILTER);
    setAppliedFilter(INITIAL_FILTER);
    void load(INITIAL_FILTER);
  }, [load]);

  const handleChange = useCallback((patch: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    const f = { ...appliedFilter, page };
    setAppliedFilter(f);
    setFilter((prev) => ({ ...prev, page }));
    void load(f);
  }, [appliedFilter, load]);

  const handleSortChange = useCallback((sortBy: string) => {
    const f = { ...appliedFilter, sortBy, page: 1 };
    setAppliedFilter(f);
    setFilter((prev) => ({ ...prev, sortBy, page: 1 }));
    void load(f);
  }, [appliedFilter, load]);

  const handleToggle = useCallback((id: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedUserIds(checked ? new Set(influencers.map((u) => u._id)) : new Set());
  }, [influencers]);

  const handleSiblingToggle = useCallback((userId: string, expanded: boolean) => {
    setExpandedSiblings((prev) => {
      const next = new Set(prev);
      expanded ? next.add(userId) : next.delete(userId);
      return next;
    });
  }, []);

  const handleAdd = useCallback(async () => {
    const ids = [...selectedUserIds];
    if (ids.length === 0 || adding) return;
    setConfirmOpen(false);
    setAdding(true);
    try {
      await addInfluencersToCampaign({ campaignId, userIds: ids });
      setSelectedUserIds(new Set());
      dispatch(showSnackbar({
        text: t(`Added ${ids.length} influencer(s) to the campaign`, `${ids.length}명을 캠페인에 추가했습니다`),
        kind: 'success',
        timestamp: Date.now(),
      }));
      onAdded(ids);
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      dispatch(showSnackbar({
        text: msg ?? t('Failed to add influencers', '인플루언서 추가에 실패했습니다'),
        kind: 'api-error',
        timestamp: Date.now(),
      }));
    } finally {
      setAdding(false);
    }
  }, [selectedUserIds, adding, campaignId, dispatch, t, onAdded, onClose]);

  // Active search conditions shown as removable chips next to the title.
  const CHIP_FIELDS: { key: StringFilterKey; label: string }[] = [
    { key: 'channelName', label: t('Name', '이름') },
    { key: 'email', label: t('Email', '이메일') },
    { key: 'country', label: t('Country', '국가') },
    { key: 'language', label: t('Language', '언어') },
    { key: 'subscriberCount', label: t('Subscribers', '구독자') },
    { key: 'keywords', label: t('Keywords', '키워드') },
    { key: 'tags', label: t('Tags', '태그') },
    { key: 'categories', label: t('Categories', '카테고리') },
    { key: 'inclusionComment', label: t('Comment', '코멘트') },
    { key: 'recentGame', label: t('Recent game', '최근 게임') },
    { key: 'recentGenre', label: t('Genre', '장르') },
    { key: 'offer', label: t('Offer', '오퍼') },
    { key: 'candidateGroupName', label: t('Group', '그룹') },
    { key: 'isRegistered', label: t('Registered', '가입 여부') },
    { key: 'hasRates', label: t('Rates', '단가') },
    { key: 'hasDemographic', label: t('Demographic', '데모그래픽') },
    { key: 'hasStreams', label: t('Streams', '스트림') },
  ];
  const textChips = CHIP_FIELDS.flatMap((f) => {
    const v = appliedFilter[f.key];
    return typeof v === 'string' && v.trim() !== '' ? [{ ...f, value: v }] : [];
  });
  const statusChips = appliedFilter.status ?? [];
  const hasChips = textChips.length > 0 || statusChips.length > 0;

  const removeTextChip = useCallback((key: StringFilterKey) => {
    const patch = { [key]: '', page: 1 } as Partial<FilterState>;
    const next = { ...appliedFilter, ...patch };
    setAppliedFilter(next);
    setFilter((prev) => ({ ...prev, ...patch }));
    void load(next);
  }, [appliedFilter, load]);

  const removeStatusChip = useCallback((s: string) => {
    const nextStatus = (appliedFilter.status ?? []).filter((x) => x !== s);
    const next = { ...appliedFilter, status: nextStatus, page: 1 };
    setAppliedFilter(next);
    setFilter((prev) => ({ ...prev, status: nextStatus, page: 1 }));
    void load(next);
  }, [appliedFilter, load]);

  const nSelected = selectedUserIds.size;

  return (
    <div className="add-influencers-drawer">
      <ChannelFilterSidebar
        searchOnly
        isCampaignMode={false}
        filter={filter}
        onChange={handleChange}
        onApply={handleApply}
        onClear={handleClear}
        isAdmin={isAdmin}
        selectedCampaignLabel=""
        campaigns={[]}
        campaignsLoading={false}
        onSelectCampaign={() => {}}
        selectedCampaignId="all"
        onOpenAdvancedFilter={() => {}}
        influencerStatuses={null}
        onStatusClick={() => {}}
        campaignAnalysis={null}
        selectedCampaign={null}
        candidateGroups={{}}
        candidateGroupsLoading={false}
        onCandidateGroupClick={() => {}}
        onCreateCandidateGroup={() => {}}
        onAddToCandidateGroup={() => {}}
        onRemoveFromCandidateGroup={() => {}}
        onRemoveCandidateGroup={() => {}}
        onCandidateGroupsCommitted={() => {}}
        selectedUserIds={selectedUserIds}
        influencers={influencers}
        onCampaignFilterChange={() => {}}
        clientHasScreenshot={false}
        onChangeClientHasScreenshot={() => {}}
      />

      <div className="add-influencers-drawer__main">
        <header className="add-influencers-drawer__header">
          <div className="add-influencers-drawer__title-wrap">
            <i className="fa fa-search-plus add-influencers-drawer__title-icon" />
            <span className="ggc-text-section-header">{t('Find & add influencers', '인플루언서 검색 추가')}</span>
            {nSelected > 0 && (
              <span className="add-influencers-drawer__count">{t(`${nSelected} selected`, `${nSelected}명 선택`)}</span>
            )}
          </div>
          {hasChips && (
            <div className="add-influencers-drawer__chips">
              {textChips.map((c) => (
                <GgcActiveFilterChip
                  key={c.key}
                  label={`${c.label}: ${c.value}`}
                  onRemove={() => removeTextChip(c.key)}
                  removeLabel={t('Remove condition', '조건 삭제')}
                />
              ))}
              {statusChips.map((s) => (
                <GgcActiveFilterChip
                  key={`status-${s}`}
                  tone="green"
                  label={`${t('Status', '상태')}: ${s}`}
                  onRemove={() => removeStatusChip(s)}
                  removeLabel={t('Remove condition', '조건 삭제')}
                />
              ))}
            </div>
          )}
          <div className="add-influencers-drawer__actions">
            <div className="add-influencers-drawer__add-wrap">
              <GgcButton
                size="sm"
                variant="primary"
                className="add-influencers-drawer__add-btn"
                disabled={nSelected === 0 || adding}
                onClick={() => setConfirmOpen((v) => !v)}
              >
                <i className="fa fa-user-plus mr-1" />
                {adding ? t('Adding...', '추가 중...') : t('Add to campaign', '캠페인에 추가')}
              </GgcButton>
              {confirmOpen && nSelected > 0 && (
                <div className="add-influencers-drawer__confirm">
                  <div className="add-influencers-drawer__confirm-text">
                    {t(
                      `Add ${nSelected} selected influencer(s) to "${campaignName}"?`,
                      `선택한 ${nSelected}명을 「${campaignName}」 캠페인에 추가할까요?`,
                    )}
                  </div>
                  <div className="add-influencers-drawer__confirm-actions">
                    <button
                      type="button"
                      className="add-influencers-drawer__confirm-btn add-influencers-drawer__confirm-btn--cancel"
                      onClick={() => setConfirmOpen(false)}
                    >
                      {t('Cancel', '취소')}
                    </button>
                    <button
                      type="button"
                      className="add-influencers-drawer__confirm-btn add-influencers-drawer__confirm-btn--confirm"
                      onClick={() => void handleAdd()}
                    >
                      {t('Add', '추가')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button type="button" className="add-influencers-drawer__close" aria-label={t('Close', '닫기')} onClick={onClose}>
              <i className="fa fa-times" />
            </button>
          </div>
        </header>

        <div className="add-influencers-drawer__body">
          {!hasSearched ? (
            <GgcEmptyState
              icon={<i className="fa fa-search" />}
              title={t('Search influencers to add', '추가할 인플루언서를 검색하세요')}
              description={t('Set filters in the left panel and press Apply to search.', '왼쪽 검색 조건을 설정한 뒤 적용을 눌러 검색하세요.')}
            />
          ) : loading ? (
            <div className="dashboard-loading"><PageLoader /></div>
          ) : (
            <InfluencerTable
              displayedInfluencers={influencers}
              influencers={influencers}
              perUserAnalysis={perUserAnalysis}
              visibleCols={DRAWER_COLUMNS}
              columns={DRAWER_COLUMNS}
              rowColumns={DRAWER_COLUMNS}
              selectedUserIds={selectedUserIds}
              onToggle={handleToggle}
              onSelectAll={handleSelectAll}
              sortBy={filter.sortBy}
              onSortChange={handleSortChange}
              page={appliedFilter.page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pageSize={pageSizeRef.current}
              isAdmin={isAdmin}
              campaigns={campaigns}
              rowSelectedCampaignId={undefined}
              organizationId={organizationId}
              computeInCampaign={(u) => !!u.campaigns?.some((c) => c.campaignId === campaignId && c.status !== 'deleted' && c.status !== 'rejected')}
              computeInCampaignDeleted={(u) => !!u.campaigns?.some((c) => c.campaignId === campaignId && (c.status === 'deleted' || c.status === 'rejected'))}
              computeInCampaignRejected={(u) => !!u.campaigns?.some((c) => c.campaignId === campaignId && c.status === 'rejected')}
              expandedSiblingParents={expandedSiblings}
              onSiblingToggle={handleSiblingToggle}
              shortSubsFormat
              showEmptyDash
              showKpiSourcePicker={false}
              emptyState={(
                <tr>
                  <td colSpan={99} className="py-8 text-center text-sm text-ggc-text-gray">
                    {t('No influencers match the search conditions.', '검색 조건에 맞는 인플루언서가 없습니다.')}
                  </td>
                </tr>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
